import React from 'react';
import {
  CourseAnalyticsLearnerDetails,
  CourseLearnersAnalyticsItem,
} from '../../../../../data/models/Gradebook/CourseAnalyticsModels';
import {analyticsText} from '../../../analyticsText';
import './styles.css';
import AnalyticsEmptyState from '../../../../../uiToolkit/Analytics/AnalyticsEmptyState';
import LearnersAnalyticsTable from '../components/LearnersAnalyticsTable';
import LearnerAnalyticsDetails from '../components/LearnerAnalyticsDetails';
import AnalyticsNavigationTabs from '../../../../../uiToolkit/Analytics/AnalyticsNavigationTabs';
import {AnalyticsTableSkeleton} from '../../../components/AnalyticsSkeletons';
import {courseLevelAnalyticsTabs} from '../../conf';

const getSortValue = (
  learner: CourseLearnersAnalyticsItem,
  field: string,
): string | number => {
  switch (field) {
    case 'Due date':
      return learner.dueDate ? new Date(learner.dueDate).getTime() : 0;
    case 'Status':
      return learner.status;
    case 'AvgScore':
      return learner.avgScore ?? -1;
    case 'Progress':
      return learner.progress;
    default:
      return learner.name.toLowerCase();
  }
};

const learnerComparator =
  (field: string, asc: boolean) =>
  (a: CourseLearnersAnalyticsItem, b: CourseLearnersAnalyticsItem) => {
    const valueA = getSortValue(a, field);
    const valueB = getSortValue(b, field);
    if (valueA === valueB) return 0;
    const result = valueA > valueB ? 1 : -1;
    return asc ? result : -result;
  };

interface Props {
  courseAnalyticsLearners: CourseLearnersAnalyticsItem[];
  // True until the learners request resolves; drives the widget preloader.
  learnersLoading: boolean;
  onBack: () => void;
  onSelectLearner: (learnerId: string) => void;
  selectedLearnerGradebook?: CourseAnalyticsLearnerDetails;
  onCloseLearnerDetails: () => void;
  onOpenLearningJourney: (learnerId: string, lessonId: string) => void;
  isGradable: boolean;
  passingScore?: number;
  search: string;
  onExport: () => void;
  setSeach: (search: string) => void;
  organizationId: string;
  courseId: string;
  onChangeSort: (field: string, asc: boolean) => void;
  sortBy: string;
  sortAsc: boolean;
  onChangeGrade: (
    learnerId: string,
    lessonId: string,
    newGrade: number,
  ) => Promise<boolean>;
}

const CourseAnalyticsLearnersUI = React.memo(
  ({
    courseAnalyticsLearners,
    learnersLoading,
    onSelectLearner,
    selectedLearnerGradebook,
    onCloseLearnerDetails,
    onOpenLearningJourney,
    isGradable,
    passingScore,
    search,
    setSeach,
    onExport,
    organizationId,
    courseId,
    onChangeSort,
    sortBy,
    sortAsc,
    onChangeGrade,
  }: Props) => {
    const getLearners = () => {
      const sorted = [...courseAnalyticsLearners].sort(
        learnerComparator(sortBy, sortAsc),
      );
      if (search) {
        return sorted.filter(x =>
          x.name.toLowerCase().includes(search.toLowerCase()),
        );
      }
      return sorted;
    };

    return (
      <div>
        <AnalyticsNavigationTabs
          onExport={onExport}
          search={search}
          setSearch={setSeach}
          tabs={courseLevelAnalyticsTabs(organizationId, courseId)}
        />

        <div>
          {isGradable ? (
            <div className="PassingScoreContainer">
              <span className="icon-icon_score"></span>
              <span className="InfoText">{analyticsText.passingScore}</span>
              <span className="ScoreText">{passingScore}</span>
            </div>
          ) : (
            <div />
          )}
          {learnersLoading ? (
            <AnalyticsTableSkeleton />
          ) : !courseAnalyticsLearners.length ? (
            <AnalyticsEmptyState />
          ) : (
            <div className="TableAndDetails">
              <LearnersAnalyticsTable
                learners={getLearners()}
                onSelectLearner={onSelectLearner}
                passingScore={passingScore || 50}
                isGradable={isGradable}
                onChangeSort={onChangeSort}
                sortField={sortBy}
                asc={sortAsc}
              />
              {selectedLearnerGradebook && (
                <LearnerAnalyticsDetails
                  key={selectedLearnerGradebook.learnerId}
                  analytics={selectedLearnerGradebook}
                  onClose={onCloseLearnerDetails}
                  onOpenLearningJourney={onOpenLearningJourney}
                  isGradable={isGradable}
                  onChangeGrade={onChangeGrade}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default CourseAnalyticsLearnersUI;
